import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sprout, Puzzle, Gem } from 'lucide-react';
import SEO from '../components/SEO';
import './Home.css';

const Home = () => {
  const features = [
    {
      icon: <Sprout size={32} />,
      title: "Authenticiteit",
      description: "Begeleiding op een oprechte en transparante manier, met echt luisteren en aandacht voor wie jij bent."
    },
    {
      icon: <Puzzle size={32} />,
      title: "Maatwerk",
      description: "Elke sessie en elk traject wordt afgestemd op jouw persoonlijke doelen en situatie, zodat je begeleiding krijgt die echt bij jou past."
    },
    {
      icon: <Gem size={32} />,
      title: "Kwaliteit",
      description: "Gecertificeerd volgens de MR-methode, met expertise in beweging, vitaliteit, stressmanagement en persoonlijke ontwikkeling."
    }
  ];

  const benefits = [
    "Dichter bij jezelf komen, je emoties beter leren herkennen en ermee omgaan.",
    "Loskomen van oude patronen en de vicieuze cirkels doorbreken.",
    "Situaties en gedragsmatige patronen herkennen en meer grip krijgen op je leven.",
    "Een betere balans vinden tussen werk en privé en een gezonde energiebalans ervaren.",
    "Ontdekken waar je energie van krijgt, helder krijgen wat je wilt en stappen durven zetten.",
    "Een positief zelfbeeld ontwikkelen, je lichamelijke conditie verbeteren en vertrouwen in jezelf vergroten."
  ];

  return (
    <div className="home">
      <SEO
        title="CtheNature - Professionele Wandelcoaching en Coaching in Nederland | Omgeving Ravenstein"
        description="Professionele wandelcoaching en coaching in Nederland. Ontdek jezelf door wandelen in de natuur. Wandelcoach voor persoonlijke ontwikkeling, stressmanagement, burn-out coaching en balans. Gratis kennismakingsgesprek. Omgeving Ravenstein."
        keywords="wandelcoaching, coaching, wandelcoach, wandelcoaching Nederland, coaching Nederland, natuurcoaching, wandelcoaching Ravenstein, wandelcoaching omgeving Ravenstein, persoonlijke ontwikkeling coaching, stressmanagement coaching, burn-out coaching, wandelen en coaching, coaching in de natuur, wandelcoaching sessies, persoonlijke coach, life coaching, balans en energie"
        url="/"
      />
      {/* Hero Section */}
                   <section className="hero">
               <div className="hero-background">
                 <img 
                  //src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                  src="/ChantalBerg.jpg"
                  alt="Wandelcoaching in de natuur - CtheNature wandelcoach in bergachtig gebied" 
                  className="hero-background-img"
                  loading="eager"
                  fetchPriority="high"
                />
                 <div className="hero-overlay"></div>
               </div>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">
              Ontdek jezelf
            </h1>
            <img src="/Logo_met_Vlinder.png" alt="CtheNature Logo" className="hero-logo" loading="eager" />
            <p className="hero-subtitle">
              Professionele wandelcoaching in Nederland die je helpt groeien, 
              reflecteren en je doelen te bereiken in de rustgevende omgeving van de natuur.
            </p>
            <div className="hero-buttons">
              <Link to="/diensten" className="btn btn-primary">
                Ontdek mijn diensten
                <ArrowRight size={20} />
              </Link>
              <Link to="/contact" className="btn btn-secondary">
                Gratis kennismaking
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section features-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Waarom Wandelcoaching?</h2>
            <p className="section-subtitle">
            Wandelcoaching is zoveel meer dan een gesprek in de natuur. Door in beweging te zijn, kom je letterlijk en figuurlijk dichter bij jezelf. Het ritme van het lopen brengt rust in je hoofd, terwijl de natuur ruimte geeft om los te laten en nieuwe inzichten op te doen.
Ik werk met de MR-methode: een combinatie van wandelen, ademhaling en mindfulness. Dat betekent dat we niet alleen praten, maar vooral ervaren. Je leert hoe ademhaling je kan helpen ontspannen, hoe je patronen kunt doorbreken en hoe je beter voelt wat er echt speelt. Vaak weet je zelf al wat je zou willen veranderen, maar het hoe is lastig, precies daar ligt de kracht van wandelcoaching.
Het is laagdrempelig, praktisch en heel effectief. Je merkt dat je energie toeneemt, je hoofd lichter voelt en je weer vertrouwen krijgt in jezelf. Of je nu herstelt van stress of burn-out, even uit balans bent, of gewoon meer rust en focus wilt in je dagelijks leven: wandelcoaching helpt je stap voor stap vooruit.

            </p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section benefits-section">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Voordelen van Wandelcoaching</h2>
              <p className="section-subtitle">
              Wandelcoaching biedt unieke voordelen die traditionele coaching niet kan bieden. De combinatie van beweging, natuur en professionele begeleiding zorgt voor optimale resultaten, zowel mentaal als fysiek.
              <br></br>
              <br></br>
Afhankelijk van jouw coachvraag, kun je tijdens een wandelcoachingstraject:

              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    {benefit}
                  </motion.li>
                ))}
              </ul>
              <Link to="/diensten" className="btn btn-primary">
                Bekijk mijn diensten
              </Link>
            </motion.div>
                               <motion.div
                     className="benefits-image"
                     initial={{ opacity: 0, x: 50 }}
                     whileInView={{ opacity: 1, x: 0 }}
                     transition={{ duration: 0.8 }}
                     viewport={{ once: true }}
                   >
                     <img 
                      //src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                      src="/ChantalBerg.jpg"
                      alt="Wandelpad door het bos" 
                      className="benefits-image-img"
                      loading="lazy"
                    />
                   </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">Klaar om te beginnen?</h2>
            <p className="cta-subtitle">
              Neem contact op voor een gratis kennismakingsgesprek en ontdek hoe 
              wandelcoaching jou kan helpen groeien.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Neem contact op
              </Link>
              <Link to="/over-ons" className="btn btn-secondary">
                Meer over mij
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
